import HaspObject from './HaspObject'
import { routeOrthogonalConnection } from '../draw/connectionGeometry'
import { splitColorOpa } from '../utils/color.js'
import ConnectionRouteShape from '../draw/ConnectionRouteShape'
import ConnectionFlowShape from '../draw/ConnectionFlowShape'
import { assignConnectionReferences, normalizeLegacyConnectionConfig } from '../draw/connectionModel'
import { canonicalConnectionData } from '../draw/connectionData.js'
import { connectionSelectionHitWidth } from '../draw/badgeConnectionInteraction'
import { resolveConnectionFlow } from '../draw/connectionFlow'
import { connectionFlowInput, getEffectiveConnectionValue } from '../draw/connectionEffectiveValue.js'
import { getObjectPage } from '../utils/hasputils'

const CONNECTION_LABEL_FRACTIONS = [0.35, 0.5, 0.65]

function connectionPathKey(points) {
    if (!Array.isArray(points) || points.length < 2) return null
    const forward = points.map(point => `${point.x},${point.y}`).join(';')
    const reverse = [...points].reverse().map(point => `${point.x},${point.y}`).join(';')
    return forward < reverse ? forward : reverse
}

function connectionPathPointAtFraction(points, fraction) {
    if (!Array.isArray(points) || points.length < 2) return null

    let totalLength = 0
    for (let index = 1; index < points.length; index += 1) {
        const previous = points[index - 1]
        const current = points[index]
        totalLength += Math.hypot(current.x - previous.x, current.y - previous.y)
    }
    if (totalLength === 0) return { x: points[0].x, y: points[0].y }

    let distance = totalLength * Math.max(0, Math.min(1, fraction))
    for (let index = 1; index < points.length; index += 1) {
        const previous = points[index - 1]
        const current = points[index]
        const segmentLength = Math.hypot(current.x - previous.x, current.y - previous.y)
        if (distance <= segmentLength) {
            const ratio = segmentLength === 0 ? 0 : distance / segmentLength
            return {
                x: previous.x + (current.x - previous.x) * ratio,
                y: previous.y + (current.y - previous.y) * ratio,
            }
        }
        distance -= segmentLength
    }

    const last = points[points.length - 1]
    return { x: last.x, y: last.y }
}

function compareConnections(left, right) {
    const leftId = Number(left.haspid)
    const rightId = Number(right.haspid)
    if (Number.isFinite(leftId) && Number.isFinite(rightId) && leftId !== rightId) {
        return leftId - rightId
    }
    return String(left.id()).localeCompare(String(right.id()))
}

export default class HaspConnection extends HaspObject {
    constructor(config) {
        const normalizedConfig = normalizeLegacyConnectionConfig({ ...config })
        super({ ...normalizedConfig, x: 0, y: 0, width: 1, height: 1 })
        assignConnectionReferences(this, normalizedConfig)
        Object.assign(this, canonicalConnectionData(normalizedConfig))
        this._showConnectionId = Boolean(config.show_id)
        this.rect.visible(false)
        this.rect.listening(false)
        this.shadow.visible(false)
        this.idTextObj.visible(false)
        this.draggable(false)
        this.listening(true)
        this.routePoints = []
        this.resolvedFromSide = null
        this.resolvedToSide = null
        this.flowPhase = 0
        this._isSelected = false

        // Connections do not use the Transformer because their geometry is
        // derived from their badge endpoints. Keep a separate, non-listening
        // route for the selected state so a connection clicked on the canvas
        // has clear visual feedback like other design objects.
        this.selectionShape = new ConnectionRouteShape({
            name: 'connection-selection-shape',
            obj: 'connection_selection',
            stroke: '#38bdf8',
            strokeWidth: 5,
            listening: false,
            visible: false,
            lineCap: 'round',
            lineJoin: 'round',
        })
        this.routeShape = new ConnectionRouteShape({
            name: 'connection-shape',
            obj: 'connection_path',
            hitStrokeWidth: 14,
            listening: false,
            lineCap: 'round',
            lineJoin: 'round',
        })
        this.hitShape = new ConnectionRouteShape({
            name: 'connection-hit-shape',
            obj: 'connection_path_hit',
            stroke: 'rgba(0,0,0,0)',
            strokeWidth: 14,
            hitStrokeWidth: 14,
            listening: true,
            lineCap: 'round',
            lineJoin: 'round',
        })
        this.flowShape = new ConnectionFlowShape({
            name: 'connection-flow-shape', obj: 'connection_flow', listening: false,
        })
        this.add(this.selectionShape, this.routeShape, this.flowShape, this.hitShape)
        this.routeShape.moveToTop()
        this.flowShape.moveToTop()
        this.hitShape.moveToTop()
        for (const property of ['from', 'to', 'from_side', 'to_side']) {
            this.on(property, () => this.requestConnectionRefresh())
        }
        this.on('corner_radius', () => this.updateRouteShapes())
        for (const property of ['line_width', 'line_color']) this.on(property, () => this.applyAppearance())
        for (const property of ['min', 'max', 'value', 'value_source']) {
            this.on(property, () => {
                Object.assign(this, canonicalConnectionData(this))
                this.updateFlowState()
            })
        }
        for (const property of ['animation_enabled', 'bullet_size', 'bullet_count']) {
            this.on(property, () => this.updateFlowState())
        }
        this.on('bullet_color', () => this.applyAppearance())
        this.applyAppearance()
        this.updateFlowState()
    }

    getResizeMode() { return 'none' }

    showIdHandler(e) {
        super.showIdHandler(e)
        this._showConnectionId = Boolean(e.evt.showId)
        this.updateConnectionIdLabel()
    }

    updateId(id) {
        super.updateId(id)
        this.updateConnectionIdLabel()
    }

    updateConnectionIdLabel() {
        const routeKey = connectionPathKey(this.routePoints)
        if (!routeKey) {
            this.setConnectionIdLabelPosition(null)
            this.getLayer()?.batchDraw()
            return
        }

        const page = getObjectPage(this)
        const sharedConnections = (this.getLayer()?.find?.(node => node.obj === 'connection') ?? [])
            .filter(connection => getObjectPage(connection) === page
                && connectionPathKey(connection.routePoints) === routeKey)
            .sort(compareConnections)
        const connectionIndex = Math.max(0, sharedConnections.indexOf(this))
        const fraction = CONNECTION_LABEL_FRACTIONS[connectionIndex % CONNECTION_LABEL_FRACTIONS.length]
        sharedConnections.forEach((connection, index) => {
            connection.setConnectionIdLabelPosition(
                CONNECTION_LABEL_FRACTIONS[index % CONNECTION_LABEL_FRACTIONS.length],
            )
        })
        // A route update can add/remove a shared-route peer, so update all
        // labels in the group even when only this connection moved.
        if (!sharedConnections.includes(this)) this.setConnectionIdLabelPosition(fraction)
        this.getLayer()?.batchDraw()
    }

    setConnectionIdLabelPosition(fraction) {
        const label = this.idTextObj
        const point = fraction === null
            ? null
            : connectionPathPointAtFraction(this.routePoints, fraction)
        const visible = this._showConnectionId && point !== null
        label.visible(visible)
        if (!visible) {
            return
        }

        // Keep the shared object-ID label styling, but anchor this label at a
        // measured fraction of the routed path rather than at the connection
        // group's origin. It remains non-listening, so the hit shape underneath
        // keeps the connection selectable.
        label.text(String(this.haspid))
        label.offsetX(label.width() / 2)
        label.offsetY(label.height() / 2)
        label.position(point)
        label.moveToTop()
    }

    setSelected(selected) {
        this._isSelected = Boolean(selected)
        this.selectionShape.strokeWidth(Math.max(4, (Number(this.line_width) || 1) + 4))
        this.selectionShape.visible(this._isSelected && this.routePoints.length >= 2)
        this.getLayer()?.batchDraw()
    }

    objectExport(_page, objectData) {
        delete objectData.x
        delete objectData.y
        delete objectData.w
        delete objectData.h
    }

    getClientRect(config = {}) {
        if (this.routePoints.length === 0) return { x: 0, y: 0, width: 0, height: 0 }
        const xs = this.routePoints.map(point => point.x)
        const ys = this.routePoints.map(point => point.y)
        const pad = (Number(this.line_width) || 1) / 2
        return {
            x: Math.min(...xs) - pad,
            y: Math.min(...ys) - pad,
            width: Math.max(...xs) - Math.min(...xs) + pad * 2,
            height: Math.max(...ys) - Math.min(...ys) + pad * 2,
        }
    }

    applyAppearance() {
        const color = splitColorOpa(this.line_color || '#ffffffff')
        this.routeShape.stroke(color.color)
        this.routeShape.opacity(color.opacity)
        this.routeShape.strokeWidth(Math.max(1, Number(this.line_width) || 1))
        const bulletColor = splitColorOpa(this.bullet_color || this.line_color || '#ffffffff')
        this.flowShape.fill(bulletColor.color)
        this.flowShape.opacity(bulletColor.opacity)
        this.selectionShape.strokeWidth(Math.max(4, (Number(this.line_width) || 1) + 4))
        const hitWidth = connectionSelectionHitWidth(this.line_width)
        this.hitShape.strokeWidth(hitWidth)
        this.hitShape.hitStrokeWidth(hitWidth)
        this.getLayer()?.refreshBadgeRings?.()
    }

    updateRouteShapes() {
        this.selectionShape.setRoute(this.routePoints, this.corner_radius)
        this.routeShape.setRoute(this.routePoints, this.corner_radius)
        this.hitShape.setRoute(this.routePoints, this.corner_radius)
        this.flowShape.setRoute(this.routePoints, this.corner_radius)
        this.selectionShape.visible(this._isSelected && this.routePoints.length >= 2)
        this.updateFlowState()
        this.updateConnectionIdLabel()
    }

    getEffectiveData() {
        const flow = resolveConnectionFlow(connectionFlowInput(this), this.connectionMaxSpeed())
        return { min: flow.min, max: flow.max, value: flow.value, normalized: flow.normalized, speed: flow.speed }
    }

    getEffectiveValue() {
        return getEffectiveConnectionValue(this)
    }

    connectionMaxSpeed() {
        return this.getLayer()?.getAttr?.('connection_max_speed') ?? 120
    }

    updateFlowState() {
        this.flowState = resolveConnectionFlow(connectionFlowInput(this), this.connectionMaxSpeed())
        this.flowShape?.setFlow({
            phase: this.flowPhase,
            bulletSize: this.bullet_size,
            bulletCount: this.bullet_count,
            visible: this.flowState.moving,
        })
        if (this.flowState.moving) this.getLayer()?.requestConnectionAnimation?.()
        this.getLayer()?.refreshBadgeRings?.()
        this.getLayer()?.batchDraw()
    }

    advanceConnectionAnimation(deltaSeconds) {
        if (!this.flowState?.moving || !this.flowShape?.connectionPath.length) return false
        this.flowPhase += this.flowState.speed * Math.max(0, Number(deltaSeconds) || 0)
        this.flowShape.setFlow({
            phase: this.flowPhase,
            bulletSize: this.bullet_size,
            bulletCount: this.bullet_count,
            visible: true,
        })
        return true
    }

    setResolvedRoute(fromAttachment, toAttachment) {
        if (!fromAttachment || !toAttachment) {
            this.routePoints = []
            this.selectionShape.setRoute([])
            this.routeShape.setRoute([])
            this.hitShape.setRoute([])
            this.flowShape.setRoute([], this.corner_radius)
            this.updateFlowState()
            this.updateConnectionIdLabel()
            return
        }
        this.resolvedFromSide = fromAttachment.side
        this.resolvedToSide = toAttachment.side
        this.routePoints = routeOrthogonalConnection(
            fromAttachment.point, fromAttachment.side,
            toAttachment.point, toAttachment.side,
        )
        this.updateRouteShapes()
        this.applyAppearance()
    }

    setResolvedGeometry(fromAttachment, toAttachment, points, status = 'resolved', conflicts = []) {
        if (!fromAttachment || !toAttachment || points.length < 2) {
            this.routePoints = []
            this.selectionShape.setRoute([])
            this.routeShape.setRoute([])
            this.hitShape.setRoute([])
            this.flowShape.setRoute([], this.corner_radius)
            this.updateFlowState()
            this.updateConnectionIdLabel()
            this.routingStatus = status
            this.routingConflicts = conflicts
            return
        }
        this.resolvedFromSide = fromAttachment.side
        this.resolvedToSide = toAttachment.side
        this.routePoints = points
        this.routingStatus = status
        this.routingConflicts = conflicts
        this.updateRouteShapes()
        this.applyAppearance()
    }

    requestConnectionRefresh() {
        const layer = this.getLayer()
        if (layer) layer.refreshBadgeConnections()
    }
}
